import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Settings, Calendar, Image, Users, Heart, MessageSquare, Home, LayoutGrid,
  Plus, Trash2, Edit2, Save, X, ChevronDown, ChevronUp, Upload, LogOut, Lock,
  UserCheck, UserX, Shield, FolderOpen, FileText, Award
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_jci-luxury-portal/artifacts/914ad0rv_Artboard%201.png";

export default function AdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('homepage');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    settings: null,
    about: null,
    banners: [],
    stats: [],
    sections: [],
    features: [],
    events: [],
    amenities: [],
    gallery: [],
    galleryCategories: [],
    csr: [],
    tiers: [],
    inquiries: [],
    adminUsers: [],
  });

  const tabs = [
    { id: 'homepage', label: 'Homepage', icon: Home },
    { id: 'about', label: 'About Page', icon: FileText },
    { id: 'settings', label: 'Site Settings', icon: Settings },
    { id: 'events', label: 'Events', icon: Calendar },
    { id: 'amenities', label: 'Amenities', icon: Image },
    { id: 'gallery', label: 'Gallery', icon: FolderOpen },
    { id: 'csr', label: 'CSR', icon: Heart },
    { id: 'tiers', label: 'Membership Tiers', icon: LayoutGrid },
    { id: 'benefits', label: 'Member Benefits', icon: Award },
    { id: 'inquiries', label: 'Inquiries', icon: MessageSquare },
    ...(user?.role === 'super_admin' ? [{ id: 'users', label: 'Admin Users', icon: Users }] : []),
    { id: 'password', label: 'Change Password', icon: Lock },
  ];

  useEffect(() => {
    document.title = 'Club JCI Ltd - Admin Panel';
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const endpoints = [
        axios.get(`${API}/settings`),
        axios.get(`${API}/about`),
        axios.get(`${API}/banners`),
        axios.get(`${API}/homepage-stats`),
        axios.get(`${API}/homepage-sections`),
        axios.get(`${API}/feature-items`),
        axios.get(`${API}/events`),
        axios.get(`${API}/amenities`),
        axios.get(`${API}/gallery`),
        axios.get(`${API}/gallery-categories`),
        axios.get(`${API}/csr`),
        axios.get(`${API}/membership-tiers`),
        axios.get(`${API}/inquiries`),
        axios.get(`${API}/member-benefits`),
      ];
      
      if (user?.role === 'super_admin') {
        endpoints.push(axios.get(`${API}/admin/users`));
      }

      const results = await Promise.all(endpoints);
      
      setData({
        settings: results[0].data,
        about: results[1].data,
        banners: results[2].data,
        stats: results[3].data,
        sections: results[4].data,
        features: results[5].data,
        events: results[6].data,
        amenities: results[7].data,
        gallery: results[8].data,
        galleryCategories: results[9].data,
        csr: results[10].data,
        tiers: results[11].data,
        inquiries: results[12].data,
        memberBenefits: results[13].data,
        adminUsers: results[14]?.data || [],
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  // Image Upload Component
  const ImageUploader = ({ value, onChange, label }) => {
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const handleUpload = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await axios.post(`${API}/upload/image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        onChange(res.data.url);
        toast.success('Image uploaded');
      } catch (error) {
        toast.error('Upload failed');
      } finally {
        setUploading(false);
      }
    };

    const imageUrl = value?.startsWith('/api/images/') 
      ? `${process.env.REACT_APP_BACKEND_URL}${value}/data` 
      : value;

    return (
      <div>
        <label className="form-label">{label}</label>
        <div className="flex gap-3 items-start">
          <div className="flex-1">
            <input
              type="text"
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              className="w-full rounded-sm text-sm"
              placeholder="Image URL or upload"
            />
          </div>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="btn-gold text-xs py-2 px-3 rounded-sm flex items-center gap-1"
          >
            <Upload size={14} />
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleUpload}
            className="hidden"
          />
        </div>
        {imageUrl && (
          <img src={imageUrl} alt="Preview" className="mt-2 h-20 w-auto rounded-sm object-cover" />
        )}
      </div>
    );
  };

  // Homepage Tab
  const HomepageTab = () => {
    return (
      <div className="space-y-8">
        {/* Banners Section */}
        <div className="glass-card p-6 rounded-sm">
          <h3 className="text-xl font-semibold text-[#F1F5F9] mb-4">Hero Banners</h3>
          <BannersList />
        </div>

        {/* Stats Section */}
        <div className="glass-card p-6 rounded-sm">
          <h3 className="text-xl font-semibold text-[#F1F5F9] mb-4">Stats Section</h3>
          <StatsList />
        </div>

        {/* Section Titles */}
        <div className="glass-card p-6 rounded-sm">
          <h3 className="text-xl font-semibold text-[#F1F5F9] mb-4">Section Titles</h3>
          <SectionsList />
        </div>

        {/* Feature Items */}
        <div className="glass-card p-6 rounded-sm">
          <h3 className="text-xl font-semibold text-[#F1F5F9] mb-4">Feature Items</h3>
          <FeaturesList />
        </div>
      </div>
    );
  };

  // Banners List
  const BannersList = () => {
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ title: '', subtitle: '', image_url: '', button_text: '', button_link: '', order: 0, is_active: true });
    const [editingId, setEditingId] = useState(null);

    const handleSave = async () => {
      try {
        if (editingId) {
          await axios.put(`${API}/banners/${editingId}`, form);
        } else {
          await axios.post(`${API}/banners`, form);
        }
        toast.success(editingId ? 'Banner updated' : 'Banner created');
        setShowForm(false);
        setEditingId(null);
        setForm({ title: '', subtitle: '', image_url: '', button_text: '', button_link: '', order: 0, is_active: true });
        fetchAllData();
      } catch (error) {
        toast.error('Failed to save');
      }
    };

    const handleDelete = async (id) => {
      if (!window.confirm('Delete this banner?')) return;
      await axios.delete(`${API}/banners/${id}`);
      toast.success('Banner deleted');
      fetchAllData();
    };

    const handleEdit = (banner) => {
      setForm(banner);
      setEditingId(banner.id);
      setShowForm(true);
    };

    return (
      <div>
        <button onClick={() => { setShowForm(!showForm); setEditingId(null); setForm({ title: '', subtitle: '', image_url: '', button_text: '', button_link: '', order: 0, is_active: true }); }} className="btn-gold text-xs py-2 px-4 rounded-sm mb-4 flex items-center gap-2">
          {showForm ? <X size={14} /> : <Plus size={14} />}
          {showForm ? 'Cancel' : 'Add Banner'}
        </button>

        {showForm && (
          <div className="bg-[#172A45] p-4 rounded-sm mb-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Title</label>
                <input type="text" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="w-full rounded-sm" />
              </div>
              <div>
                <label className="form-label">Order</label>
                <input type="number" value={form.order} onChange={(e) => setForm({...form, order: parseInt(e.target.value) || 0})} className="w-full rounded-sm" />
              </div>
            </div>
            <div>
              <label className="form-label">Subtitle</label>
              <input type="text" value={form.subtitle} onChange={(e) => setForm({...form, subtitle: e.target.value})} className="w-full rounded-sm" />
            </div>
            <ImageUploader value={form.image_url} onChange={(url) => setForm({...form, image_url: url})} label="Banner Image" />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Button Text</label>
                <input type="text" value={form.button_text} onChange={(e) => setForm({...form, button_text: e.target.value})} className="w-full rounded-sm" />
              </div>
              <div>
                <label className="form-label">Button Link</label>
                <input type="text" value={form.button_link} onChange={(e) => setForm({...form, button_link: e.target.value})} className="w-full rounded-sm" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({...form, is_active: e.target.checked})} className="w-4 h-4" />
              <label className="text-[#94A3B8] text-sm">Active</label>
            </div>
            <button onClick={handleSave} className="btn-gold-filled text-xs py-2 px-4 rounded-sm">{editingId ? 'Update' : 'Create'}</button>
          </div>
        )}

        <div className="space-y-3">
          {data.banners.map(banner => (
            <div key={banner.id} className="flex items-center justify-between bg-[#172A45] p-3 rounded-sm">
              <div className="flex items-center gap-3">
                {banner.image_url && (
                  <img 
                    src={banner.image_url.startsWith('/api/images/') ? `${process.env.REACT_APP_BACKEND_URL}${banner.image_url}/data` : banner.image_url} 
                    alt="" 
                    className="w-16 h-10 object-cover rounded-sm" 
                  />
                )}
                <div>
                  <p className="text-[#F1F5F9] font-medium">{banner.title}</p>
                  <p className="text-[#94A3B8] text-xs">{banner.is_active ? 'Active' : 'Inactive'}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => handleEdit(banner)} className="p-1 text-[#94A3B8] hover:text-[#D4AF37]"><Edit2 size={14} /></button>
                <button onClick={() => handleDelete(banner.id)} className="p-1 text-[#94A3B8] hover:text-red-400"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Stats List
  const StatsList = () => {
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({});

    const handleSave = async (id) => {
      await axios.put(`${API}/homepage-stats/${id}`, form);
      toast.success('Stat updated');
      setEditingId(null);
      fetchAllData();
    };

    const handleAdd = async () => {
      await axios.post(`${API}/homepage-stats`, { number: 'New', label: 'Label', order: data.stats.length + 1 });
      toast.success('Stat added');
      fetchAllData();
    };

    const handleDelete = async (id) => {
      await axios.delete(`${API}/homepage-stats/${id}`);
      toast.success('Stat deleted');
      fetchAllData();
    };

    return (
      <div>
        <button onClick={handleAdd} className="btn-gold text-xs py-2 px-4 rounded-sm mb-4 flex items-center gap-2">
          <Plus size={14} /> Add Stat
        </button>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {data.stats.map(stat => (
            <div key={stat.id} className="bg-[#172A45] p-4 rounded-sm">
              {editingId === stat.id ? (
                <div className="space-y-2">
                  <input type="text" value={form.number ?? stat.number} onChange={(e) => setForm({...form, number: e.target.value})} className="w-full rounded-sm text-sm" placeholder="Number" />
                  <input type="text" value={form.label ?? stat.label} onChange={(e) => setForm({...form, label: e.target.value})} className="w-full rounded-sm text-sm" placeholder="Label" />
                  <div className="flex gap-2">
                    <button onClick={() => handleSave(stat.id)} className="btn-gold-filled text-xs py-1 px-3 rounded-sm">Save</button>
                    <button onClick={() => setEditingId(null)} className="btn-gold text-xs py-1 px-3 rounded-sm">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="flex justify-between">
                  <div>
                    <p className="text-2xl font-bold text-[#D4AF37]">{stat.number}</p>
                    <p className="text-[#94A3B8] text-sm">{stat.label}</p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditingId(stat.id); setForm(stat); }} className="p-1 text-[#94A3B8] hover:text-[#D4AF37]"><Edit2 size={14} /></button>
                    <button onClick={() => handleDelete(stat.id)} className="p-1 text-[#94A3B8] hover:text-red-400"><Trash2 size={14} /></button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Sections List
  const SectionsList = () => {
    const [editingKey, setEditingKey] = useState(null);
    const [form, setForm] = useState({});

    const handleSave = async (key) => {
      await axios.put(`${API}/homepage-sections/${key}`, form);
      toast.success('Section updated');
      setEditingKey(null);
      fetchAllData();
    };

    return (
      <div className="space-y-4">
        {data.sections.map(section => (
          <div key={section.section_key} className="bg-[#172A45] p-4 rounded-sm">
            <p className="text-[#D4AF37] text-xs uppercase tracking-wider mb-2">{section.section_key.replace(/_/g, ' ')}</p>
            {editingKey === section.section_key ? (
              <div className="space-y-2">
                <input type="text" value={form.title ?? section.title} onChange={(e) => setForm({...form, title: e.target.value})} className="w-full rounded-sm text-sm" placeholder="Title" />
                <textarea value={form.subtitle ?? section.subtitle} onChange={(e) => setForm({...form, subtitle: e.target.value})} className="w-full rounded-sm text-sm" placeholder="Subtitle" rows={2} />
                <div className="flex gap-2">
                  <button onClick={() => handleSave(section.section_key)} className="btn-gold-filled text-xs py-1 px-3 rounded-sm">Save</button>
                  <button onClick={() => setEditingKey(null)} className="btn-gold text-xs py-1 px-3 rounded-sm">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-[#F1F5F9] font-medium">{section.title}</p>
                  <p className="text-[#94A3B8] text-sm">{section.subtitle}</p>
                </div>
                <button onClick={() => { setEditingKey(section.section_key); setForm(section); }} className="p-1 text-[#94A3B8] hover:text-[#D4AF37]"><Edit2 size={14} /></button>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Features List
  const FeaturesList = () => {
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ icon: 'Users', title: '', description: '', order: 0 });
    const [editingId, setEditingId] = useState(null);

    const iconOptions = ['Users', 'Calendar', 'Utensils', 'Award', 'Heart', 'Star', 'Globe', 'Briefcase'];

    const handleSave = async () => {
      if (editingId) {
        await axios.put(`${API}/feature-items/${editingId}`, form);
      } else {
        await axios.post(`${API}/feature-items`, form);
      }
      toast.success(editingId ? 'Updated' : 'Created');
      setShowForm(false);
      setEditingId(null);
      setForm({ icon: 'Users', title: '', description: '', order: 0 });
      fetchAllData();
    };

    const handleDelete = async (id) => {
      await axios.delete(`${API}/feature-items/${id}`);
      toast.success('Deleted');
      fetchAllData();
    };

    return (
      <div>
        <button onClick={() => { setShowForm(!showForm); setEditingId(null); }} className="btn-gold text-xs py-2 px-4 rounded-sm mb-4 flex items-center gap-2">
          {showForm ? <X size={14} /> : <Plus size={14} />}
          {showForm ? 'Cancel' : 'Add Feature'}
        </button>

        {showForm && (
          <div className="bg-[#172A45] p-4 rounded-sm mb-4 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Icon</label>
                <select value={form.icon} onChange={(e) => setForm({...form, icon: e.target.value})} className="w-full rounded-sm">
                  {iconOptions.map(icon => <option key={icon} value={icon}>{icon}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Order</label>
                <input type="number" value={form.order} onChange={(e) => setForm({...form, order: parseInt(e.target.value) || 0})} className="w-full rounded-sm" />
              </div>
            </div>
            <div>
              <label className="form-label">Title</label>
              <input type="text" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="w-full rounded-sm" />
            </div>
            <div>
              <label className="form-label">Description</label>
              <textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="w-full rounded-sm" rows={2} />
            </div>
            <button onClick={handleSave} className="btn-gold-filled text-xs py-2 px-4 rounded-sm">{editingId ? 'Update' : 'Create'}</button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {data.features.map(feature => (
            <div key={feature.id} className="bg-[#172A45] p-3 rounded-sm flex justify-between items-start">
              <div>
                <p className="text-[#D4AF37] text-xs">{feature.icon}</p>
                <p className="text-[#F1F5F9] font-medium">{feature.title}</p>
                <p className="text-[#94A3B8] text-sm">{feature.description}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setForm(feature); setEditingId(feature.id); setShowForm(true); }} className="p-1 text-[#94A3B8] hover:text-[#D4AF37]"><Edit2 size={14} /></button>
                <button onClick={() => handleDelete(feature.id)} className="p-1 text-[#94A3B8] hover:text-red-400"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Settings Tab
  const SettingsTab = () => {
    const [form, setForm] = useState(data.settings || {});

    const handleSave = async () => {
      await axios.put(`${API}/settings`, form);
      toast.success('Settings saved');
      fetchAllData();
    };

    useEffect(() => {
      if (data.settings) setForm(data.settings);
    }, [data.settings]);

    const ToggleSwitch = ({ label, checked, onChange }) => (
      <div className="flex items-center justify-between py-3 px-4 bg-[#0A1628] rounded-sm">
        <span className="text-[#F1F5F9]">{label}</span>
        <button
          onClick={() => onChange(!checked)}
          className={`relative w-12 h-6 rounded-full transition-colors ${checked ? 'bg-[#D4AF37]' : 'bg-[#374151]'}`}
        >
          <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${checked ? 'left-7' : 'left-1'}`} />
        </button>
      </div>
    );

    return (
      <div className="space-y-6">
        <div className="glass-card p-6 rounded-sm">
          <h3 className="text-xl font-semibold text-[#F1F5F9] mb-6">Site Settings</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Club Name</label>
                <input type="text" value={form.club_name || ''} onChange={(e) => setForm({...form, club_name: e.target.value})} className="w-full rounded-sm" />
              </div>
              <div>
                <label className="form-label">Tagline</label>
                <input type="text" value={form.tagline || ''} onChange={(e) => setForm({...form, tagline: e.target.value})} className="w-full rounded-sm" />
              </div>
            </div>
            <ImageUploader value={form.logo_url} onChange={(url) => setForm({...form, logo_url: url})} label="Logo URL" />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Phone 1</label>
                <input type="text" value={form.phone1 || ''} onChange={(e) => setForm({...form, phone1: e.target.value})} className="w-full rounded-sm" />
              </div>
              <div>
                <label className="form-label">Phone 2</label>
                <input type="text" value={form.phone2 || ''} onChange={(e) => setForm({...form, phone2: e.target.value})} className="w-full rounded-sm" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Email 1</label>
                <input type="email" value={form.email1 || ''} onChange={(e) => setForm({...form, email1: e.target.value})} className="w-full rounded-sm" />
              </div>
              <div>
                <label className="form-label">Email 2</label>
                <input type="email" value={form.email2 || ''} onChange={(e) => setForm({...form, email2: e.target.value})} className="w-full rounded-sm" />
              </div>
            </div>
            <div>
              <label className="form-label">Address</label>
              <input type="text" value={form.address || ''} onChange={(e) => setForm({...form, address: e.target.value})} className="w-full rounded-sm" />
            </div>
            <div>
              <label className="form-label">External Login URL</label>
              <input type="url" value={form.external_login_url || ''} onChange={(e) => setForm({...form, external_login_url: e.target.value})} className="w-full rounded-sm" placeholder="https://member-portal.com" />
            </div>
            <div>
              <label className="form-label">Hero Title</label>
              <input type="text" value={form.hero_title || ''} onChange={(e) => setForm({...form, hero_title: e.target.value})} className="w-full rounded-sm" />
            </div>
            <div>
              <label className="form-label">Hero Subtitle</label>
              <textarea value={form.hero_subtitle || ''} onChange={(e) => setForm({...form, hero_subtitle: e.target.value})} className="w-full rounded-sm" rows={2} />
            </div>
          </div>
        </div>

        <div className="glass-card p-6 rounded-sm">
          <h3 className="text-xl font-semibold text-[#F1F5F9] mb-6">Visibility Settings</h3>
          <p className="text-sm text-[#94A3B8] mb-4">Toggle which sections are visible on the website</p>
          <div className="space-y-3">
            <ToggleSwitch 
              label="Member Login Button" 
              checked={form.show_member_login !== false} 
              onChange={(val) => setForm({...form, show_member_login: val})} 
            />
            <ToggleSwitch 
              label="Hero Banner Section" 
              checked={form.show_hero_section !== false} 
              onChange={(val) => setForm({...form, show_hero_section: val})} 
            />
            <ToggleSwitch 
              label="Stats Section" 
              checked={form.show_stats_section !== false} 
              onChange={(val) => setForm({...form, show_stats_section: val})} 
            />
            <ToggleSwitch 
              label="Features Section" 
              checked={form.show_features_section !== false} 
              onChange={(val) => setForm({...form, show_features_section: val})} 
            />
            <ToggleSwitch 
              label="Amenities Preview Section" 
              checked={form.show_amenities_section !== false} 
              onChange={(val) => setForm({...form, show_amenities_section: val})} 
            />
            <ToggleSwitch 
              label="Gallery Section" 
              checked={form.show_gallery_section !== false} 
              onChange={(val) => setForm({...form, show_gallery_section: val})} 
            />
            <ToggleSwitch 
              label="CTA Section" 
              checked={form.show_cta_section !== false} 
              onChange={(val) => setForm({...form, show_cta_section: val})} 
            />
          </div>
        </div>

        <button onClick={handleSave} className="btn-gold-filled text-sm py-3 px-6 rounded-sm flex items-center gap-2">
          <Save size={16} /> Save All Settings
        </button>
      </div>
    );
  };

  // About Page Tab
  const AboutTab = () => {
    const [form, setForm] = useState(data.about || {});

    const handleSave = async () => {
      await axios.put(`${API}/about`, form);
      toast.success('About page content saved');
      fetchAllData();
    };

    useEffect(() => {
      if (data.about) setForm(data.about);
    }, [data.about]);

    return (
      <div className="glass-card p-6 rounded-sm">
        <h3 className="text-xl font-semibold text-[#F1F5F9] mb-6">About Page Content</h3>
        <div className="space-y-6">
          <div>
            <label className="form-label">Our Story</label>
            <textarea 
              value={form.story || ''} 
              onChange={(e) => setForm({...form, story: e.target.value})} 
              className="w-full rounded-sm" 
              rows={4}
              placeholder="Tell the story of Club JCI..."
            />
            <p className="text-xs text-[#94A3B8] mt-1">This appears in the hero section of the About page</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="form-label">Mission Statement</label>
              <textarea 
                value={form.mission || ''} 
                onChange={(e) => setForm({...form, mission: e.target.value})} 
                className="w-full rounded-sm" 
                rows={4}
                placeholder="Our mission is to..."
              />
            </div>
            <div>
              <label className="form-label">Vision Statement</label>
              <textarea 
                value={form.vision || ''} 
                onChange={(e) => setForm({...form, vision: e.target.value})} 
                className="w-full rounded-sm" 
                rows={4}
                placeholder="Our vision for the future..."
              />
            </div>
          </div>

          <div>
            <label className="form-label">Core Values (comma-separated)</label>
            <input 
              type="text" 
              value={Array.isArray(form.values) ? form.values.join(', ') : (form.values || '')} 
              onChange={(e) => setForm({...form, values: e.target.value.split(',').map(v => v.trim()).filter(Boolean)})} 
              className="w-full rounded-sm"
              placeholder="Excellence, Community, Privacy, Impact"
            />
            <p className="text-xs text-[#94A3B8] mt-1">Values displayed in the Core Values section</p>
          </div>

          <button onClick={handleSave} className="btn-gold-filled text-sm py-3 px-6 rounded-sm flex items-center gap-2">
            <Save size={16} /> Save About Content
          </button>
        </div>
      </div>
    );
  };

  // Events Tab  
  const EventsTab = () => {
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ title: '', description: '', date: '', time: '', venue: 'Club JCI Ltd', image_url: '', contact_number: '', is_featured: false });
    const [editingId, setEditingId] = useState(null);

    const handleSave = async () => {
      if (editingId) {
        await axios.put(`${API}/events/${editingId}`, form);
      } else {
        await axios.post(`${API}/events`, form);
      }
      toast.success(editingId ? 'Event updated' : 'Event created');
      setShowForm(false);
      setEditingId(null);
      setForm({ title: '', description: '', date: '', time: '', venue: 'Club JCI Ltd', image_url: '', contact_number: '', is_featured: false });
      fetchAllData();
    };

    const handleDelete = async (id) => {
      if (!window.confirm('Delete this event?')) return;
      await axios.delete(`${API}/events/${id}`);
      toast.success('Event deleted');
      fetchAllData();
    };

    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-[#F1F5F9]">Events</h3>
          <button onClick={() => { setShowForm(!showForm); setEditingId(null); setForm({ title: '', description: '', date: '', time: '', venue: 'Club JCI Ltd', image_url: '', contact_number: '', is_featured: false }); }} className="btn-gold text-xs py-2 px-4 rounded-sm flex items-center gap-2">
            {showForm ? <X size={14} /> : <Plus size={14} />}
            {showForm ? 'Cancel' : 'Add Event'}
          </button>
        </div>

        {showForm && (
          <div className="glass-card p-6 rounded-sm mb-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Title</label>
                <input type="text" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="w-full rounded-sm" />
              </div>
              <div>
                <label className="form-label">Venue</label>
                <input type="text" value={form.venue} onChange={(e) => setForm({...form, venue: e.target.value})} className="w-full rounded-sm" />
              </div>
            </div>
            <div>
              <label className="form-label">Description</label>
              <textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="w-full rounded-sm" rows={3} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="form-label">Date</label>
                <input type="text" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} className="w-full rounded-sm" placeholder="March 15, 2026" />
              </div>
              <div>
                <label className="form-label">Time</label>
                <input type="text" value={form.time} onChange={(e) => setForm({...form, time: e.target.value})} className="w-full rounded-sm" placeholder="7:00 PM" />
              </div>
              <div>
                <label className="form-label">Contact Number</label>
                <input type="text" value={form.contact_number} onChange={(e) => setForm({...form, contact_number: e.target.value})} className="w-full rounded-sm" placeholder="+880..." />
              </div>
            </div>
            <ImageUploader value={form.image_url} onChange={(url) => setForm({...form, image_url: url})} label="Event Image" />
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({...form, is_featured: e.target.checked})} className="w-4 h-4" />
              <label className="text-[#94A3B8] text-sm">Featured Event</label>
            </div>
            <button onClick={handleSave} className="btn-gold-filled text-xs py-2 px-6 rounded-sm">{editingId ? 'Update' : 'Create'}</button>
          </div>
        )}

        <div className="space-y-4">
          {data.events.map(event => (
            <div key={event.id} className="glass-card p-4 rounded-sm">
              <div className="flex gap-4">
                {event.image_url && (
                  <img 
                    src={event.image_url.startsWith('/api/images/') ? `${process.env.REACT_APP_BACKEND_URL}${event.image_url}/data` : event.image_url} 
                    alt="" 
                    className="w-24 h-20 object-cover rounded-sm" 
                  />
                )}
                <div className="flex-1">
                  <div className="flex justify-between">
                    <div>
                      <h4 className="text-[#F1F5F9] font-medium">{event.title}</h4>
                      <p className="text-[#94A3B8] text-sm">{event.date} at {event.time}</p>
                      {event.contact_number && <p className="text-[#D4AF37] text-xs">Contact: {event.contact_number}</p>}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => { setForm(event); setEditingId(event.id); setShowForm(true); }} className="p-1 text-[#94A3B8] hover:text-[#D4AF37]"><Edit2 size={14} /></button>
                      <button onClick={() => handleDelete(event.id)} className="p-1 text-[#94A3B8] hover:text-red-400"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Gallery Tab with Categories
  const GalleryTab = () => {
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ title: '', image_url: '', category_id: '', order: 0 });
    const [editingId, setEditingId] = useState(null);
    const [newCategory, setNewCategory] = useState('');

    const handleSave = async () => {
      if (editingId) {
        await axios.put(`${API}/gallery/${editingId}`, form);
      } else {
        await axios.post(`${API}/gallery`, form);
      }
      toast.success(editingId ? 'Updated' : 'Created');
      setShowForm(false);
      setEditingId(null);
      setForm({ title: '', image_url: '', category_id: '', order: 0 });
      fetchAllData();
    };

    const handleDelete = async (id) => {
      await axios.delete(`${API}/gallery/${id}`);
      toast.success('Deleted');
      fetchAllData();
    };

    const handleAddCategory = async () => {
      if (!newCategory.trim()) return;
      await axios.post(`${API}/gallery-categories`, { name: newCategory, order: data.galleryCategories.length + 1 });
      toast.success('Category added');
      setNewCategory('');
      fetchAllData();
    };

    const handleDeleteCategory = async (id) => {
      await axios.delete(`${API}/gallery-categories/${id}`);
      toast.success('Category deleted');
      fetchAllData();
    };

    return (
      <div className="space-y-6">
        {/* Categories Management */}
        <div className="glass-card p-4 rounded-sm">
          <h4 className="text-lg font-semibold text-[#F1F5F9] mb-4">Gallery Categories</h4>
          <div className="flex gap-2 mb-4">
            <input type="text" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} placeholder="New category name" className="flex-1 rounded-sm text-sm" />
            <button onClick={handleAddCategory} className="btn-gold text-xs py-2 px-4 rounded-sm">Add</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.galleryCategories.map(cat => (
              <span key={cat.id} className="bg-[#172A45] text-[#F1F5F9] px-3 py-1 rounded-sm text-sm flex items-center gap-2">
                {cat.name}
                <button onClick={() => handleDeleteCategory(cat.id)} className="text-red-400 hover:text-red-300"><X size={12} /></button>
              </span>
            ))}
          </div>
        </div>

        {/* Gallery Items */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-[#F1F5F9]">Gallery Items</h3>
            <button onClick={() => setShowForm(!showForm)} className="btn-gold text-xs py-2 px-4 rounded-sm flex items-center gap-2">
              {showForm ? <X size={14} /> : <Plus size={14} />}
              {showForm ? 'Cancel' : 'Add Item'}
            </button>
          </div>

          {showForm && (
            <div className="glass-card p-4 rounded-sm mb-4 space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Title</label>
                  <input type="text" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="w-full rounded-sm" />
                </div>
                <div>
                  <label className="form-label">Category</label>
                  <select value={form.category_id} onChange={(e) => setForm({...form, category_id: e.target.value})} className="w-full rounded-sm">
                    <option value="">Select category</option>
                    {data.galleryCategories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                  </select>
                </div>
              </div>
              <ImageUploader value={form.image_url} onChange={(url) => setForm({...form, image_url: url})} label="Image" />
              <button onClick={handleSave} className="btn-gold-filled text-xs py-2 px-4 rounded-sm">{editingId ? 'Update' : 'Create'}</button>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {data.gallery.map(item => (
              <div key={item.id} className="glass-card rounded-sm overflow-hidden group relative">
                <img 
                  src={item.image_url.startsWith('/api/images/') ? `${process.env.REACT_APP_BACKEND_URL}${item.image_url}/data` : item.image_url} 
                  alt={item.title} 
                  className="w-full aspect-square object-cover" 
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button onClick={() => { setForm(item); setEditingId(item.id); setShowForm(true); }} className="p-2 bg-[#D4AF37] rounded-full"><Edit2 size={14} className="text-[#051025]" /></button>
                  <button onClick={() => handleDelete(item.id)} className="p-2 bg-red-500 rounded-full"><Trash2 size={14} className="text-white" /></button>
                </div>
                <div className="p-2">
                  <p className="text-[#F1F5F9] text-sm truncate">{item.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Membership Tiers Tab (with editable benefits)
  const TiersTab = () => {
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({});
    const [newBenefit, setNewBenefit] = useState('');

    const handleSave = async (id) => {
      await axios.put(`${API}/membership-tiers/${id}`, form);
      toast.success('Tier updated');
      setEditingId(null);
      setForm({});
      fetchAllData();
    };

    const addBenefit = () => {
      if (!newBenefit.trim()) return;
      const benefits = [...(form.benefits || []), newBenefit];
      setForm({...form, benefits});
      setNewBenefit('');
    };

    const removeBenefit = (index) => {
      const benefits = form.benefits.filter((_, i) => i !== index);
      setForm({...form, benefits});
    };

    return (
      <div>
        <h3 className="text-xl font-semibold text-[#F1F5F9] mb-6">Membership Tiers</h3>
        <div className="space-y-4">
          {data.tiers.map(tier => (
            <div key={tier.id} className="glass-card p-6 rounded-sm">
              {editingId === tier.id ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="form-label">Name</label>
                      <input type="text" value={form.name ?? tier.name} onChange={(e) => setForm({...form, name: e.target.value})} className="w-full rounded-sm" />
                    </div>
                    <div>
                      <label className="form-label">Entry Fee (BDT)</label>
                      <input type="number" value={form.entry_fee ?? tier.entry_fee} onChange={(e) => setForm({...form, entry_fee: parseInt(e.target.value) || 0})} className="w-full rounded-sm" />
                    </div>
                    <div>
                      <label className="form-label">Monthly Fee (BDT)</label>
                      <input type="number" value={form.monthly_fee ?? tier.monthly_fee} onChange={(e) => setForm({...form, monthly_fee: parseInt(e.target.value) || 0})} className="w-full rounded-sm" />
                    </div>
                  </div>
                  <div>
                    <label className="form-label">Benefits</label>
                    <div className="space-y-2 mb-2">
                      {(form.benefits || tier.benefits).map((benefit, i) => (
                        <div key={i} className="flex gap-2 items-center">
                          <input type="text" value={benefit} onChange={(e) => {
                            const benefits = [...(form.benefits || tier.benefits)];
                            benefits[i] = e.target.value;
                            setForm({...form, benefits});
                          }} className="flex-1 rounded-sm text-sm" />
                          <button onClick={() => removeBenefit(i)} className="text-red-400"><X size={16} /></button>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input type="text" value={newBenefit} onChange={(e) => setNewBenefit(e.target.value)} placeholder="Add benefit" className="flex-1 rounded-sm text-sm" />
                      <button onClick={addBenefit} className="btn-gold text-xs py-1 px-3 rounded-sm">Add</button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={form.is_featured ?? tier.is_featured} onChange={(e) => setForm({...form, is_featured: e.target.checked})} className="w-4 h-4" />
                    <label className="text-[#94A3B8] text-sm">Featured (Recommended)</label>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleSave(tier.id)} className="btn-gold-filled text-xs py-2 px-4 rounded-sm">Save</button>
                    <button onClick={() => { setEditingId(null); setForm({}); }} className="btn-gold text-xs py-2 px-4 rounded-sm">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="text-[#F1F5F9] font-semibold text-lg">{tier.name}</h4>
                    <p className="text-[#D4AF37]">{tier.entry_fee.toLocaleString()} BDT entry • {tier.monthly_fee === 0 ? 'No monthly fee' : `${tier.monthly_fee.toLocaleString()} BDT/month`}</p>
                    <ul className="mt-2 space-y-1">
                      {tier.benefits.map((b, i) => <li key={i} className="text-[#94A3B8] text-sm">• {b}</li>)}
                    </ul>
                  </div>
                  <button onClick={() => { setEditingId(tier.id); setForm({...tier}); }} className="p-2 text-[#94A3B8] hover:text-[#D4AF37]"><Edit2 size={16} /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Amenities Tab
  const AmenitiesTab = () => {
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ name: '', description: '', image_url: '', is_upcoming: false, order: 0 });
    const [editingId, setEditingId] = useState(null);

    const handleSave = async () => {
      if (editingId) {
        await axios.put(`${API}/amenities/${editingId}`, form);
      } else {
        await axios.post(`${API}/amenities`, form);
      }
      toast.success(editingId ? 'Updated' : 'Created');
      setShowForm(false);
      setEditingId(null);
      setForm({ name: '', description: '', image_url: '', is_upcoming: false, order: 0 });
      fetchAllData();
    };

    const handleDelete = async (id) => {
      await axios.delete(`${API}/amenities/${id}`);
      toast.success('Deleted');
      fetchAllData();
    };

    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-[#F1F5F9]">Amenities</h3>
          <button onClick={() => setShowForm(!showForm)} className="btn-gold text-xs py-2 px-4 rounded-sm flex items-center gap-2">
            {showForm ? <X size={14} /> : <Plus size={14} />}
            {showForm ? 'Cancel' : 'Add Amenity'}
          </button>
        </div>

        {showForm && (
          <div className="glass-card p-4 rounded-sm mb-4 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Name</label>
                <input type="text" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="w-full rounded-sm" />
              </div>
              <div>
                <label className="form-label">Order</label>
                <input type="number" value={form.order} onChange={(e) => setForm({...form, order: parseInt(e.target.value) || 0})} className="w-full rounded-sm" />
              </div>
            </div>
            <div>
              <label className="form-label">Description</label>
              <textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="w-full rounded-sm" rows={2} />
            </div>
            <ImageUploader value={form.image_url} onChange={(url) => setForm({...form, image_url: url})} label="Image" />
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={form.is_upcoming} onChange={(e) => setForm({...form, is_upcoming: e.target.checked})} className="w-4 h-4" />
              <label className="text-[#94A3B8] text-sm">Upcoming (Coming Soon)</label>
            </div>
            <button onClick={handleSave} className="btn-gold-filled text-xs py-2 px-4 rounded-sm">{editingId ? 'Update' : 'Create'}</button>
          </div>
        )}

        <div className="space-y-3">
          {data.amenities.map(amenity => (
            <div key={amenity.id} className="glass-card p-4 rounded-sm flex gap-4">
              {amenity.image_url && (
                <img 
                  src={amenity.image_url.startsWith('/api/images/') ? `${process.env.REACT_APP_BACKEND_URL}${amenity.image_url}/data` : amenity.image_url} 
                  alt="" 
                  className="w-24 h-20 object-cover rounded-sm" 
                />
              )}
              <div className="flex-1">
                <div className="flex justify-between">
                  <div>
                    <h4 className="text-[#F1F5F9] font-medium">{amenity.name}</h4>
                    <p className="text-[#94A3B8] text-sm line-clamp-2">{amenity.description}</p>
                    {amenity.is_upcoming && <span className="text-[#D4AF37] text-xs">Coming Soon</span>}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => { setForm(amenity); setEditingId(amenity.id); setShowForm(true); }} className="p-1 text-[#94A3B8] hover:text-[#D4AF37]"><Edit2 size={14} /></button>
                    <button onClick={() => handleDelete(amenity.id)} className="p-1 text-[#94A3B8] hover:text-red-400"><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // CSR Tab
  const CSRTab = () => {
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ title: '', description: '', image_url: '', order: 0 });
    const [editingId, setEditingId] = useState(null);

    const handleSave = async () => {
      if (editingId) {
        await axios.put(`${API}/csr/${editingId}`, form);
      } else {
        await axios.post(`${API}/csr`, form);
      }
      toast.success(editingId ? 'Updated' : 'Created');
      setShowForm(false);
      setEditingId(null);
      setForm({ title: '', description: '', image_url: '', order: 0 });
      fetchAllData();
    };

    const handleDelete = async (id) => {
      await axios.delete(`${API}/csr/${id}`);
      toast.success('Deleted');
      fetchAllData();
    };

    return (
      <div>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-[#F1F5F9]">CSR Initiatives</h3>
          <button onClick={() => setShowForm(!showForm)} className="btn-gold text-xs py-2 px-4 rounded-sm flex items-center gap-2">
            {showForm ? <X size={14} /> : <Plus size={14} />}
            {showForm ? 'Cancel' : 'Add Initiative'}
          </button>
        </div>

        {showForm && (
          <div className="glass-card p-4 rounded-sm mb-4 space-y-3">
            <div>
              <label className="form-label">Title</label>
              <input type="text" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="w-full rounded-sm" />
            </div>
            <div>
              <label className="form-label">Description</label>
              <textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="w-full rounded-sm" rows={3} />
            </div>
            <ImageUploader value={form.image_url} onChange={(url) => setForm({...form, image_url: url})} label="Image" />
            <button onClick={handleSave} className="btn-gold-filled text-xs py-2 px-4 rounded-sm">{editingId ? 'Update' : 'Create'}</button>
          </div>
        )}

        <div className="space-y-3">
          {data.csr.map(item => (
            <div key={item.id} className="glass-card p-4 rounded-sm flex gap-4">
              {item.image_url && (
                <img 
                  src={item.image_url.startsWith('/api/images/') ? `${process.env.REACT_APP_BACKEND_URL}${item.image_url}/data` : item.image_url} 
                  alt="" 
                  className="w-24 h-20 object-cover rounded-sm" 
                />
              )}
              <div className="flex-1 flex justify-between">
                <div>
                  <h4 className="text-[#F1F5F9] font-medium">{item.title}</h4>
                  <p className="text-[#94A3B8] text-sm">{item.description}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { setForm(item); setEditingId(item.id); setShowForm(true); }} className="p-1 text-[#94A3B8] hover:text-[#D4AF37]"><Edit2 size={14} /></button>
                  <button onClick={() => handleDelete(item.id)} className="p-1 text-[#94A3B8] hover:text-red-400"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Member Benefits Tab
  const BenefitsTab = () => {
    const [showForm, setShowForm] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState({ title: '', description: '', order: 0 });

    const handleSubmit = async () => {
      if (!form.title || !form.description) {
        toast.error('Please fill all fields');
        return;
      }
      
      if (editId) {
        await axios.put(`${API}/member-benefits/${editId}`, form);
        toast.success('Benefit updated');
      } else {
        await axios.post(`${API}/member-benefits`, form);
        toast.success('Benefit added');
      }
      setForm({ title: '', description: '', order: 0 });
      setEditId(null);
      setShowForm(false);
      fetchAllData();
    };

    const handleEdit = (benefit) => {
      setForm({ title: benefit.title, description: benefit.description, order: benefit.order });
      setEditId(benefit.id);
      setShowForm(true);
    };

    const handleDelete = async (id) => {
      if (!window.confirm('Delete this benefit?')) return;
      await axios.delete(`${API}/member-benefits/${id}`);
      toast.success('Deleted');
      fetchAllData();
    };

    return (
      <div className="space-y-6">
        <div className="glass-card p-6 rounded-sm">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-[#F1F5F9]">Member Benefits</h3>
            <button
              onClick={() => { setShowForm(!showForm); setEditId(null); setForm({ title: '', description: '', order: 0 }); }}
              className="btn-gold-filled text-sm py-2 px-4 rounded-sm flex items-center gap-2"
            >
              <Plus size={16} /> {showForm ? 'Cancel' : 'Add Benefit'}
            </button>
          </div>

          {showForm && (
            <div className="mb-6 p-4 bg-[#0A1628] rounded-sm space-y-4">
              <div>
                <label className="form-label">Title</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({...form, title: e.target.value})}
                  className="w-full rounded-sm"
                  placeholder="e.g. Exclusive Events"
                />
              </div>
              <div>
                <label className="form-label">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({...form, description: e.target.value})}
                  className="w-full rounded-sm"
                  rows={2}
                  placeholder="e.g. Access to members-only events and gatherings"
                />
              </div>
              <div>
                <label className="form-label">Order</label>
                <input
                  type="number"
                  value={form.order}
                  onChange={(e) => setForm({...form, order: parseInt(e.target.value) || 0})}
                  className="w-24 rounded-sm"
                />
              </div>
              <button onClick={handleSubmit} className="btn-gold-filled text-sm py-2 px-4 rounded-sm flex items-center gap-2">
                <Save size={16} /> {editId ? 'Update' : 'Add'} Benefit
              </button>
            </div>
          )}

          <div className="space-y-3">
            {(data.memberBenefits || []).map((benefit) => (
              <div key={benefit.id} className="flex items-center justify-between p-4 bg-[#0A1628] rounded-sm">
                <div className="flex-1">
                  <h4 className="text-[#D4AF37] font-medium">{benefit.title}</h4>
                  <p className="text-sm text-[#94A3B8]">{benefit.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleEdit(benefit)} className="p-2 text-[#94A3B8] hover:text-[#D4AF37]">
                    <Edit2 size={16} />
                  </button>
                  <button onClick={() => handleDelete(benefit.id)} className="p-2 text-[#94A3B8] hover:text-red-400">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Inquiries Tab
  const InquiriesTab = () => {
    const [expandedId, setExpandedId] = useState(null);

    const handleDelete = async (id) => {
      if (!window.confirm('Delete this inquiry?')) return;
      await axios.delete(`${API}/inquiries/${id}`);
      toast.success('Deleted');
      fetchAllData();
    };

    return (
      <div>
        <h3 className="text-xl font-semibold text-[#F1F5F9] mb-6">Membership Inquiries</h3>
        {data.inquiries.length === 0 ? (
          <div className="glass-card p-8 rounded-sm text-center">
            <p className="text-[#94A3B8]">No inquiries yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {data.inquiries.map(inquiry => (
              <div key={inquiry.id} className="glass-card rounded-sm overflow-hidden">
                <div className="p-4 cursor-pointer flex justify-between items-center" onClick={() => setExpandedId(expandedId === inquiry.id ? null : inquiry.id)}>
                  <div>
                    <h4 className="text-[#F1F5F9] font-medium">{inquiry.full_name}</h4>
                    <p className="text-[#94A3B8] text-sm">{inquiry.email} • {inquiry.membership_interest}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded-sm ${inquiry.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-green-500/20 text-green-400'}`}>{inquiry.status}</span>
                    {expandedId === inquiry.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                </div>
                {expandedId === inquiry.id && (
                  <div className="px-4 pb-4 border-t border-[rgba(212,175,55,0.1)]">
                    <div className="grid grid-cols-2 gap-4 py-3 text-sm">
                      <div><span className="text-[#94A3B8]">Phone:</span> <span className="text-[#F1F5F9]">{inquiry.phone}</span></div>
                      <div><span className="text-[#94A3B8]">Profession:</span> <span className="text-[#F1F5F9]">{inquiry.profession}</span></div>
                      {inquiry.company && <div><span className="text-[#94A3B8]">Company:</span> <span className="text-[#F1F5F9]">{inquiry.company}</span></div>}
                      <div><span className="text-[#94A3B8]">Submitted:</span> <span className="text-[#F1F5F9]">{new Date(inquiry.created_at).toLocaleDateString()}</span></div>
                    </div>
                    {inquiry.message && <p className="text-[#94A3B8] text-sm py-2 border-t border-[rgba(212,175,55,0.1)]">{inquiry.message}</p>}
                    <button onClick={() => handleDelete(inquiry.id)} className="text-red-400 text-xs flex items-center gap-1 mt-2"><Trash2 size={12} /> Delete</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Admin Users Tab
  const UsersTab = () => {
    const roleColors = { super_admin: 'text-[#D4AF37]', admin: 'text-blue-400', editor: 'text-green-400' };
    const statusColors = { approved: 'bg-green-500/20 text-green-400', pending: 'bg-yellow-500/20 text-yellow-400', rejected: 'bg-red-500/20 text-red-400' };

    const handleApprove = async (id) => {
      await axios.put(`${API}/admin/users/${id}`, { status: 'approved' });
      toast.success('User approved');
      fetchAllData();
    };

    const handleReject = async (id) => {
      await axios.put(`${API}/admin/users/${id}`, { status: 'rejected' });
      toast.success('User rejected');
      fetchAllData();
    };

    const handleChangeRole = async (id, role) => {
      await axios.put(`${API}/admin/users/${id}`, { role });
      toast.success('Role updated');
      fetchAllData();
    };

    const handleDelete = async (id) => {
      if (!window.confirm('Delete this user?')) return;
      await axios.delete(`${API}/admin/users/${id}`);
      toast.success('User deleted');
      fetchAllData();
    };

    return (
      <div>
        <h3 className="text-xl font-semibold text-[#F1F5F9] mb-6">Admin Users</h3>
        <div className="space-y-3">
          {data.adminUsers.map(adminUser => (
            <div key={adminUser.id} className="glass-card p-4 rounded-sm">
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="text-[#F1F5F9] font-medium">{adminUser.username}</h4>
                    <span className={`text-xs ${roleColors[adminUser.role]}`}>{adminUser.role}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-sm ${statusColors[adminUser.status]}`}>{adminUser.status}</span>
                  </div>
                  <p className="text-[#94A3B8] text-xs mt-1">Created: {new Date(adminUser.created_at).toLocaleDateString()}</p>
                </div>
                {adminUser.id !== user?.id && (
                  <div className="flex gap-2">
                    {adminUser.status === 'pending' && (
                      <>
                        <button onClick={() => handleApprove(adminUser.id)} className="p-1 text-green-400 hover:text-green-300" title="Approve"><UserCheck size={16} /></button>
                        <button onClick={() => handleReject(adminUser.id)} className="p-1 text-red-400 hover:text-red-300" title="Reject"><UserX size={16} /></button>
                      </>
                    )}
                    <select 
                      value={adminUser.role} 
                      onChange={(e) => handleChangeRole(adminUser.id, e.target.value)} 
                      className="bg-[#172A45] text-[#F1F5F9] text-xs rounded-sm px-2 py-1 border border-[rgba(212,175,55,0.2)]"
                    >
                      <option value="editor">Editor</option>
                      <option value="admin">Admin</option>
                      <option value="super_admin">Super Admin</option>
                    </select>
                    <button onClick={() => handleDelete(adminUser.id)} className="p-1 text-red-400 hover:text-red-300"><Trash2 size={16} /></button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Password Change Tab
  const PasswordTab = () => {
    const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
    const [saving, setSaving] = useState(false);

    const handleChange = async () => {
      if (form.new_password !== form.confirm_password) {
        toast.error('Passwords do not match');
        return;
      }
      setSaving(true);
      try {
        await axios.post(`${API}/auth/change-password`, {
          current_password: form.current_password,
          new_password: form.new_password
        });
        toast.success('Password changed successfully');
        setForm({ current_password: '', new_password: '', confirm_password: '' });
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to change password');
      } finally {
        setSaving(false);
      }
    };

    return (
      <div className="glass-card p-6 rounded-sm max-w-md">
        <h3 className="text-xl font-semibold text-[#F1F5F9] mb-6">Change Password</h3>
        <div className="space-y-4">
          <div>
            <label className="form-label">Current Password</label>
            <input type="password" value={form.current_password} onChange={(e) => setForm({...form, current_password: e.target.value})} className="w-full rounded-sm" />
          </div>
          <div>
            <label className="form-label">New Password</label>
            <input type="password" value={form.new_password} onChange={(e) => setForm({...form, new_password: e.target.value})} className="w-full rounded-sm" />
          </div>
          <div>
            <label className="form-label">Confirm New Password</label>
            <input type="password" value={form.confirm_password} onChange={(e) => setForm({...form, confirm_password: e.target.value})} className="w-full rounded-sm" />
          </div>
          <button onClick={handleChange} disabled={saving} className="btn-gold-filled text-sm py-3 px-6 rounded-sm">
            {saving ? 'Changing...' : 'Change Password'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#051025]">
      {/* Header */}
      <div className="bg-[#0A192F] border-b border-[rgba(212,175,55,0.1)] py-4 px-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Club JCI Ltd" className="h-10 w-auto" />
            <div>
              <h1 className="text-lg font-semibold text-[#F1F5F9]">Admin Panel</h1>
              <p className="text-[#94A3B8] text-xs">Welcome, {user?.username}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="btn-gold text-xs py-2 px-4 rounded-sm flex items-center gap-2">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="glass-card rounded-sm overflow-hidden sticky top-24">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`tab-${tab.id}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                    activeTab === tab.id
                      ? 'bg-[#D4AF37]/10 text-[#D4AF37] border-l-2 border-[#D4AF37]'
                      : 'text-[#94A3B8] hover:bg-[#172A45] hover:text-[#F1F5F9]'
                  }`}
                >
                  <tab.icon size={16} />
                  <span className="text-sm">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-4">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="spinner"></div>
              </div>
            ) : (
              <>
                {activeTab === 'homepage' && <HomepageTab />}
                {activeTab === 'about' && <AboutTab />}
                {activeTab === 'settings' && <SettingsTab />}
                {activeTab === 'events' && <EventsTab />}
                {activeTab === 'amenities' && <AmenitiesTab />}
                {activeTab === 'gallery' && <GalleryTab />}
                {activeTab === 'csr' && <CSRTab />}
                {activeTab === 'tiers' && <TiersTab />}
                {activeTab === 'benefits' && <BenefitsTab />}
                {activeTab === 'inquiries' && <InquiriesTab />}
                {activeTab === 'users' && <UsersTab />}
                {activeTab === 'password' && <PasswordTab />}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
